<template>
  <div class="v3-widget v3-panel v3-tt" :class="{ 'v3-tt--manager': manager }" v-testid="testid">
    <div class="v3-panel-head">
      <h3 v-testid="testid + '-title'">{{ title }}</h3>
      <span v-if="tt.scoped.value.length" class="v3-count">{{ tt.scoped.value.length }}</span>
      <span class="spacer"></span>
      <span v-if="tt.searchOpen.value" class="v3-tl-search">
        <span class="ms">search</span>
        <input v-model="tt.q.value" :placeholder="tt.searchPlaceholder.value" :aria-label="tt.searchPlaceholder.value" v-testid="testid + '-search'" />
        <button class="v3-ghostbtn" type="button" aria-label="Close search" v-testid="testid + '-search-close'" @click="tt.closeSearch()">
          <span class="ms">close</span>
        </button>
      </span>
      <button v-else class="v3-chipbtn" type="button" title="Search" v-testid="testid + '-search-open'" @click="tt.searchOpen.value = true">
        <span class="ms">search</span>
      </button>
    </div>

    <div class="v3-tt-tabs" role="tablist">
      <button
        v-for="s in tt.tabs.value"
        :key="s.id"
        class="v3-tt-tab"
        :class="{ 'is-on': tt.activeTab.value === s.id }"
        type="button"
        role="tab"
        :aria-selected="tt.activeTab.value === s.id"
        v-testid="testid + '-tab-' + s.id"
        @click="tt.activeTab.value = s.id"
      >
        {{ s.label }} <span class="n">{{ s.count }}</span>
      </button>
    </div>

    <V3Skeleton v-if="tt.isLoading.value && !tt.taskCount.value" shape="list" :heading="false" />
    <div v-else-if="!tt.filtered.value.length" class="v3-task-empty" v-testid="testid + '-empty'">{{ tt.emptyText.value }}</div>

    <template v-else>
      <div class="v3-tt-headrow">
        <span>{{ manager ? 'Due' : 'Activity' }}</span>
        <span>Document</span>
        <span v-if="!manager">Evaluated by</span>
        <span>Status</span>
        <span v-if="!manager">Reference</span>
        <span></span>
      </div>

      <div v-if="tt.showAll.value && tt.filtered.value.length > limit" class="v3-tt-foot v3-tt-foot--top">
        <span>Showing all {{ tt.filtered.value.length }}</span>
        <button class="v3-tt-showall" type="button" v-testid="testid + '-show-less'" @click="tt.showAll.value = false">
          <span class="ms">expand_less</span> Show less
        </button>
      </div>

      <div
        v-for="t in tt.shownRows.value"
        :key="t.document_task_id || t.doc_id || t.reference"
        class="v3-tt-row"
        role="button"
        tabindex="0"
        v-testid="testid + '-row-' + (t.document_task_id || t.doc_id || t.reference)"
        @click="open(t)"
      >
        <div class="v3-tt-cell v3-tt-activity">
          <div class="v3-tt-when" :class="{ 'v3-due--over': tt.isOverdue(t) }">{{ tt.activityWhen(t) }}</div>
          <div v-if="tt.activitySub(t)" class="v3-tt-sub" :class="{ 'v3-due--over': tt.isOverdue(t) }">{{ tt.activitySub(t) }}</div>
        </div>
        <div class="v3-tt-cell v3-tt-doc">
          <div class="v3-tt-docname">{{ t.doc_title || t.doc_name || t.reference }}</div>
          <div v-if="tt.docSub(t)" class="v3-tt-doctype" :class="{ 'v3-due--over': tt.isAnomaly(t) }">{{ tt.docSub(t) }}</div>
        </div>
        <div v-if="!manager" class="v3-tt-cell v3-tt-owner">
          <span class="v3-avatar" :title="tt.ownerName(t)">{{ tt.initials(tt.ownerName(t)) }}</span>
          <span class="v3-tt-ownername">{{ tt.ownerName(t) || 'Unassigned' }}</span>
        </div>
        <div class="v3-tt-cell v3-tt-status">
          <span class="v3-task-pill" :style="{ '--status-c': tt.statusColour(t) }">
            <span class="ms">{{ tt.statusIcon(t) }}</span>{{ tt.statusLabel(t) }}
          </span>
        </div>
        <div v-if="!manager" class="v3-tt-cell v3-tt-ref">{{ t.reference || '—' }}</div>
        <div class="v3-tt-cell v3-tt-act">
          <button class="v3-tt-btn" :class="tt.actionKind(t)" type="button" @click.stop="open(t)">
            {{ tt.actionLabel(t) }}
          </button>
        </div>
      </div>

      <div class="v3-tt-foot">
        <span>
          Showing {{ tt.shownRows.value.length }} of {{ tt.filtered.value.length }} {{ tt.footerNoun.value }}{{ tt.filtered.value.length === 1 ? '' : 's' }}
          <template v-if="tt.overdueCount.value"> · <strong class="v3-due--over">{{ tt.overdueCount.value }} overdue</strong></template>
        </span>
        <button v-if="tt.filtered.value.length > limit" class="v3-tt-showall" type="button" v-testid="testid + '-show-all'" @click="tt.showAll.value = !tt.showAll.value">
          {{ tt.showAll.value ? 'Show less' : 'Show all (' + tt.filtered.value.length + ')' }}
        </button>
      </div>
    </template>
  </div>
</template>

<script>
import axios from "axios";
import { getCurrentInstance } from "vue";
import { taskTable } from "@rm-ui/homepage/taskTable";
import V3Skeleton from "./cLandingV3Skeleton";

// New-portal (v3) TaskTable — thin Vue 2 presentation over the shared
// `taskTable` composable. ONE widget, two persona modes via the `manager`
// definition flag (prototype V3TaskTable `mode`):
// - review (default): cross-user review queue (POST Report/reviewQueue);
//   columns Activity · Document · Evaluated by · Status · Reference.
// - manager: the user's own task list (Vuex tasks store, same source as
//   MyTasks); manager tabs, columns Due · Document · Status, noun "tasks".
// Sibling: rm-portal-beta TaskTableWidget.vue.
export default {
  name: "cLandingV3TaskTable",
  components: { V3Skeleton },
  props: {
    definition: { type: Object, default: () => ({}) },
    editMode: { type: Boolean, default: false },
    qaActions: { type: Array, default: () => [] },
  },
  setup(props) {
    const store = getCurrentInstance().proxy.$store;
    const tt = taskTable({
      http: axios,
      getTasks: () => store.state.tasks.taskList,
      getTasksLoading: () => store.state.tasks.isLoading,
      getLimit: () => props.definition.limit || 8,
      getScope: () => (props.definition.manager ? "mine" : props.definition.scope === "mine" ? "mine" : "all"),
      getMode: () => (props.definition.manager ? "manager" : "review"),
      getDocTypes: () => props.definition.docTypes || [],
    });
    return { tt };
  },
  computed: {
    testid() {
      return this.definition.controlType || "v3TaskTable";
    },
    manager() {
      return this.definition.manager === true;
    },
    title() {
      return this.definition.title || (this.manager ? "Your tasks" : "All Tasks (Admin View)");
    },
    limit() {
      return this.definition.limit || 8;
    },
  },
  created() {
    if (this.manager) this.$store.dispatch("tasks/init");
  },
  methods: {
    open(t) {
      if (this.editMode) return;
      const reference = t.reference || t.system_number;
      if (reference) this.$emit("openDocument", { system_number: reference, reference });
    },
  },
};
</script>

<style lang="scss" src="./v3Widgets.scss"></style>
